import BrandMark from './BrandMark';
import React,{useEffect,useState} from 'react';
import {createPortal} from 'react-dom';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  Smartphone, ChevronsLeft, ChevronsRight, RotateCcw, UserPlus, Gift, Percent, Ticket, Wallet, FileText, QrCode,
  BarChart3, Shield, LayoutDashboard, Users, ArrowRightLeft, ShoppingCart, Package,
  PieChart, Send, Zap, Award, Plug, LogOut, UsersRound, Activity, Database, MapPin, GitBranch, MessageCircle, Bot, CreditCard, BookOpen,
} from 'lucide-react';
import './Sidebar.css';
import ThemeToggle from './ThemeToggle';
import { canOpen, roleLabels } from '../services/access';

const WORK_ITEMS = [
  { to: '/', icon: LayoutDashboard, label: 'Дашборд' },
  { to: '/marketing', icon: BarChart3, label: 'Маркетинговая аналитика' },
  { to: '/reports', icon: FileText, label: 'Отчёты и выгрузки' },
  { to: '/clients', icon: Users, label: 'Клиенты' },
  { to: '/transactions', icon: ArrowRightLeft, label: 'Транзакции' },
  { to: '/orders', icon: ShoppingCart, label: 'Заказы' },
  { to: '/returns', icon: RotateCcw, label: 'Возвраты' },
  { to: '/catalog', icon: Package, label: 'Каталог' },
  { to: '/loyalty', icon: Award, label: 'Лояльность' },
  { to: '/referrals', icon: UserPlus, label: 'Реферальная программа' },
  { to: '/gift-cards', icon: Gift, label: 'Подарочные карты' },
  { to: '/promotions', icon: Percent, label: 'Акции' },
  { to: '/promocodes', icon: Ticket, label: 'Промокоды' },
  { to: '/segments', icon: PieChart, label: 'Сегменты' },
  { to: '/communications', icon: Send, label: 'Рассылки' },
  { to: '/automations', icon: Zap, label: 'Автоматизации' },
  { to: '/chains', icon: GitBranch, label: 'Цепочки' },
];

const SETTINGS_ITEMS = [
  { to: '/mobile-apps', icon: Smartphone, label: 'Мобильные приложения' },
  { to: '/integrations', icon: Plug, label: 'Интеграции' },
  { to: '/crm', icon: Database, label: 'CRM' },
  { to: '/bots', icon: Bot, label: 'Боты' },
  { to: '/entry-points', icon: QrCode, label: 'Точки входа' },
  { to: '/max', icon: MessageCircle, label: 'MAX' },
  { to: '/web-pixel', icon: Activity, label: 'Веб-пиксель' },
  { to: '/places', icon: MapPin, label: 'Точки продаж' },
  { to: '/team', icon: UsersRound, label: 'Команда' },
  { to: '/audit', icon: Shield, label: 'Журнал действий' },
  { to: '/wallet', icon: Wallet, label: 'Wallet' },
  { to: '/billing', icon: CreditCard, label: 'Биллинг и оплаты' },
  { to: '/knowledge', icon: BookOpen, label: 'База знаний' },
];

export default function Sidebar({collapsed=false,onToggle,onHome}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const {pathname}=useLocation();
  const [tooltip,setTooltip]=useState(null);
  useEffect(()=>{setTooltip(null);},[collapsed,pathname]);
  useEffect(()=>{
    if(!tooltip)return;
    const close=()=>setTooltip(null),key=e=>{if(e.key==='Escape')close();};
    window.addEventListener('scroll',close,true);window.addEventListener('resize',close);document.addEventListener('keydown',key);
    return()=>{window.removeEventListener('scroll',close,true);window.removeEventListener('resize',close);document.removeEventListener('keydown',key);};
  },[tooltip]);
  const showLabel=(event,label)=>{
    if(!collapsed)return;
    const rect=event.currentTarget.getBoundingClientRect();
    setTooltip({label,left:rect.right+10,top:Math.max(18,Math.min(window.innerHeight-18,rect.top+rect.height/2))});
  };
  const labelEvents=label=>({onMouseEnter:event=>showLabel(event,label),onMouseLeave:()=>setTooltip(null),onFocus:event=>showLabel(event,label),onBlur:()=>setTooltip(null),onClick:()=>setTooltip(null)});
  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <aside className={`sidebar ${collapsed?'is-collapsed':''}`} aria-label="Навигация Segmira">
      <Link to="/" className="sidebar-header" aria-label="Segmira — на дашборд" title="На дашборд" onClick={event=>{if(event.button===0&&!event.metaKey&&!event.ctrlKey&&!event.shiftKey&&!event.altKey)onHome?.();}}>
        <BrandMark size={34} className="sidebar-logo-icon" aria-hidden="true" />
        <span className="sidebar-logo-text">segmira</span>
      </Link>
      <nav className="sidebar-nav" id="segmira-primary-navigation" aria-label="Основное меню">
        {[{ title: 'Работа', items: WORK_ITEMS }, { title: 'Настройки', items: SETTINGS_ITEMS }].map(group => (
          <section className="sidebar-section" key={group.title} aria-label={group.title}>
            <h2 className="sidebar-section-title">{group.title}</h2>
            {group.items.filter(item => canOpen(user?.role, item.to)).map(({ to, icon: Icon, label }) => (
              <NavLink key={to} to={to} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`} end={to === '/'} aria-label={label} {...labelEvents(label)}>
                <Icon size={18} aria-hidden="true"/><span className="sidebar-link-text">{label}</span>
              </NavLink>
            ))}
          </section>
        ))}
        {user?.platform_admin&&<section className="sidebar-section" aria-label="Управление продуктом"><h2 className="sidebar-section-title">Команда Segmira</h2><NavLink to="/platform" className="sidebar-link" aria-label="Управление Segmira" {...labelEvents('Управление Segmira')}><Shield size={18} aria-hidden="true"/><span className="sidebar-link-text">Управление Segmira</span></NavLink></section>}
      </nav>
      <ThemeToggle />
      <button type="button" className="sidebar-collapse" onClick={onToggle} aria-label={collapsed?'Развернуть меню':'Свернуть меню'} title={collapsed?'Развернуть меню':'Свернуть меню'} aria-expanded={!collapsed} aria-controls="segmira-primary-navigation">{collapsed?<ChevronsRight size={18} aria-hidden="true"/>:<ChevronsLeft size={18} aria-hidden="true"/>}<span>{collapsed?'Развернуть меню':'Свернуть меню'}</span></button>
      <div className="sidebar-footer">
        <div className="sidebar-user">
          <div className="sidebar-user-avatar">{user?.name?.[0] || user?.email?.[0] || '?'}</div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{user?.name || 'Админ'}</div>
            <div className="sidebar-user-email">{user?.email}</div>
            {user?.role && (
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>
                {roleLabels[user.role] || 'Нет доступа'}
              </div>
            )}
          </div>
        </div>
        <button type="button" className="sidebar-logout" aria-label="Выйти из аккаунта" title="Выйти из аккаунта" onClick={handleLogout}><LogOut size={16} /></button>
      </div>
      {tooltip&&collapsed&&createPortal(<div className="sidebar-tooltip" role="tooltip" style={{left:tooltip.left,top:tooltip.top}}>{tooltip.label}</div>,document.body)}
    </aside>
  );
}